"use client";

import { useCallback, useSyncExternalStore } from "react";
import { readLocal, removeLocal, writeLocal } from "@/helpers/safeStorage";
import {
    CLE_DOSSIER,
    CLE_IDENTITE,
    DOSSIER_VIDE,
    IDENTITE_VIDE,
    nettoyerDossier,
    nettoyerIdentite,
} from "@/lib/tools/dossier";
import { CLE_ENTREPRISE, nettoyerEntreprise } from "@/lib/tools/entreprise";

/**
 * Ce que la rubrique sait déjà de son lecteur, sans lui demander de compte.
 *
 * TRENTE OUTILS QUI REPOSENT TRENTE FOIS LA MÊME QUESTION. Celui qui dit « je
 * fais du conseil » dans la phrase d'accueil le redit au simulateur de revenu
 * net, puis à l'outil d'ACRE, puis au comparateur de régimes. Chaque
 * calculateur du web est une île, et c'est pour cela qu'aucun ne donne
 * l'impression de vous connaître. Ici la réponse est retenue une fois et
 * proposée partout, ce qui ne coûte ni compte ni adresse e-mail.
 *
 * LE PROFIL EST DEVENU UN DOSSIER. Il ne tenait que les cinq champs de la
 * phrase d'accueil ; il tient désormais les réponses des six questions de
 * « Votre situation » (lib/tools/dossier.js), que les outils reprennent au
 * départ. La clé de stockage n'a pas changé : un lecteur qui avait rempli la
 * phrase retrouve ses réponses, et les champs nouveaux arrivent vides.
 *
 * LE STOCKAGE RESTE SUR LA MACHINE DU LECTEUR, et c'est la seule façon de
 * tenir la promesse affichée en haut de la rubrique : gratuits, sans compte.
 * Rien ne part vers un serveur tant que le lecteur ne s'est pas connecté, et
 * vider son navigateur suffit à tout effacer. Connecté, le dossier suit son
 * compte (components/Tools/ToolsDossierSync.jsx) ; l'identité, jamais.
 *
 * LE STOCKAGE LOCAL EST UNE SOURCE EXTÉRIEURE À REACT, et se lit donc avec
 * useSyncExternalStore plutôt que dans un effet. Le serveur rend le profil
 * vide, le navigateur rend le vrai dès le premier passage, et les deux se
 * réconcilient sans détour. Un onglet qui écrit prévient les autres.
 */

/**
 * Un objet JSON gardé sous une clé, lu comme une source externe.
 *
 * useSyncExternalStore compare les instantanés par identité : rendre un objet
 * neuf à chaque appel relancerait un rendu sans fin. On garde donc le dernier
 * texte lu et l'objet qui en vient, et on ne reconstruit que s'il a changé.
 */
function creerMagasin(cle, nettoyer, vide) {
    let dernierTexte = null;
    let dernier = vide;
    const abonnes = new Set();

    function lire() {
        const texte = readLocal(cle);
        if (texte === dernierTexte) return dernier;
        dernierTexte = texte;
        if (!texte) {
            dernier = vide;
            return dernier;
        }
        try {
            dernier = nettoyer(JSON.parse(texte));
        } catch (e) {
            dernier = vide;
        }
        return dernier;
    }

    function prevenir() {
        for (const abonne of abonnes) abonne();
    }

    function abonner(rappel) {
        abonnes.add(rappel);
        // Un autre onglet qui écrit déclenche « storage » ; le nôtre appelle
        // prevenir() directement, l'événement ne se déclenchant pas chez soi.
        const surStorage = (e) => {
            if (e.key === cle || e.key === null) rappel();
        };
        window.addEventListener("storage", surStorage);
        return () => {
            abonnes.delete(rappel);
            window.removeEventListener("storage", surStorage);
        };
    }

    function ecrire(objet) {
        writeLocal(cle, JSON.stringify(nettoyer(objet)));
        prevenir();
    }

    function effacer() {
        removeLocal(cle);
        prevenir();
    }

    return { lire, abonner, ecrire, effacer, serveur: () => vide };
}

const DOSSIER = creerMagasin(CLE_DOSSIER, nettoyerDossier, DOSSIER_VIDE);
const IDENTITE = creerMagasin(CLE_IDENTITE, nettoyerIdentite, IDENTITE_VIDE);

const ENTREPRISE = creerMagasin(CLE_ENTREPRISE, nettoyerEntreprise, null);

/** Pour la synchronisation avec le compte, hors composant. */
export const magasinDossier = DOSSIER;

/**
 * Le dossier et de quoi l'écrire.
 *
 * `enregistrer(champs)` fusionne et date l'écriture. `options.majLe` impose la
 * date : c'est ce que fait la synchronisation quand la version du compte
 * l'emporte, pour ne pas la renvoyer aussitôt comme une nouveauté.
 */
export default function useSolopreneurProfile() {
    const profil = useSyncExternalStore(DOSSIER.abonner, DOSSIER.lire, DOSSIER.serveur);

    const enregistrer = useCallback((champs, options = {}) => {
        const maintenant = new Date().toISOString();
        const actuel = DOSSIER.lire();
        // UN TOTAL DONNÉ AILLEURS REMPLACE LES MOIS DE L'ANNÉE. Un outil, ou
        // « Garder ces réponses », qui change le chiffre d'affaires ne sait
        // rien des mois : les garder ferait parler la page de deux chiffres
        // qui ne s'accordent plus. Le total choisi l'emporte ; les mois des
        // autres années restent.
        const annee = maintenant.slice(0, 4);
        const totalSansMois =
            !options.majLe &&
            "caCetteAnnee" in champs &&
            !("encaissements" in champs) &&
            champs.caCetteAnnee !== actuel.caCetteAnnee;
        const encaissements = totalSansMois
            ? { encaissements: Object.fromEntries(Object.entries(actuel.encaissements || {}).filter(([m]) => !m.startsWith(`${annee}-`))) }
            : {};
        DOSSIER.ecrire({
            ...actuel,
            ...encaissements,
            // Un chiffre d'affaires donné ici est daté du jour, sauf quand la
            // version vient du compte avec sa propre date.
            ...(!options.majLe && "caCetteAnnee" in champs ? { caLe: maintenant.slice(0, 10) } : {}),
            ...champs,
            majLe: options.majLe || maintenant,
        });
    }, []);

    /** Tout oublier : le lecteur repart d'un dossier vide. */
    const effacer = useCallback(() => {
        DOSSIER.ecrire({ ...DOSSIER_VIDE, majLe: new Date().toISOString() });
    }, []);

    return { profil, enregistrer, effacer };
}

/** L'identité des formulaires. Elle ne quitte jamais l'appareil. */
export function useIdentite() {
    const identite = useSyncExternalStore(IDENTITE.abonner, IDENTITE.lire, IDENTITE.serveur);
    const enregistrer = useCallback((champs) => {
        IDENTITE.ecrire({ ...IDENTITE.lire(), ...champs });
    }, []);
    const effacer = useCallback(() => IDENTITE.effacer(), []);
    return { identite, enregistrer, effacer };
}

/** L'entreprise choisie dans l'annuaire, sur cet appareil seulement. */
export function useEntrepriseChoisie() {
    const entreprise = useSyncExternalStore(ENTREPRISE.abonner, ENTREPRISE.lire, ENTREPRISE.serveur);
    const enregistrer = useCallback((e) => (e ? ENTREPRISE.ecrire(e) : ENTREPRISE.effacer()), []);
    const effacer = useCallback(() => ENTREPRISE.effacer(), []);
    return { entreprise, enregistrer, effacer };
}

/**
 * Vrai une fois l'hydratation passée. Le serveur et le premier rendu du
 * navigateur disent faux, ensuite vrai : c'est ce qui permet d'appliquer le
 * dossier sans que le HTML du serveur et celui du navigateur divergent.
 */
const rien = () => () => {};
export function useHydrate() {
    return useSyncExternalStore(
        rien,
        () => true,
        () => false,
    );
}
